import { FC } from 'react';
import { Badge, badgeVariants } from './badge';
import { cn } from '@/lib/utils';
import { type VariantProps } from "class-variance-authority";

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

interface CustomerBadgeProps {
  variant: 'priority' | 'status' | 'interaction';
  value: string;
  className?: string;
}

export const CustomerBadge: FC<CustomerBadgeProps> = ({
  variant,
  value,
  className
}) => {
  const getVariantMapping = (variant: string, value: string): BadgeVariant => {
    switch (variant) {
      case 'priority':
        switch (value.toLowerCase()) {
          case 'high':
            return 'destructive';
          case 'medium':
            return 'secondary';
          case 'low':
            return 'outline';
          default:
            return 'secondary';
        }

      case 'status':
        switch (value.toLowerCase()) {
          case 'active':
          case 'operating':
            return 'default';
          case 'inactive':
          case 'non-operating':
            return 'destructive';
          case 'pending':
            return 'secondary';
          default:
            return 'secondary';
        }

      case 'interaction':
        switch (value.toLowerCase()) {
          case 'call':
            return 'secondary';
          case 'whatsapp':
            return 'secondary';
          case 'visit':
            return 'default';
          case 'schedule':
            return 'outline';
          default:
            return 'secondary';
        }

      default:
        return 'secondary';
    }
  };

  return (
    <Badge
      variant={getVariantMapping(variant, value)}
      className={cn("text-xs font-medium", className)}
    >
      {value}
    </Badge>
  );
};

// Customer-specific priority badge
export const CustomerPriorityBadge: FC<{ priority: string; className?: string }> = ({
  priority,
  className
}) => (
  <CustomerBadge
    variant="priority"
    value={priority}
    className={className}
  />
);

// Customer-specific status badge
export const CustomerStatusBadge: FC<{ status: string; className?: string }> = ({
  status,
  className
}) => (
  <CustomerBadge
    variant="status"
    value={status}
    className={className}
  />
);

// Customer-specific interaction badge
export const CustomerInteractionBadge: FC<{ interaction: string; className?: string }> = ({
  interaction,
  className
}) => (
  <CustomerBadge
    variant="interaction"
    value={interaction}
    className={className}
  />
);